import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

const K_FACTOR = 32;

/**
 * Updates rankings for all player match-ups in the provided match data array.
 * 
 * @param {Array} matchDataArray - Array of match objects.
 * @param {string} pennantName - The name of the pennant (used as ranking key).
 */
export async function updateRankings(matchDataArray, pennantName) {

  const rankingUpdates = matchDataArray.map(async (matchObj) => {
    const { team1, team2 } = matchObj;

    const t1Players =
      Array.isArray(team1.playerIds) && team1.playerIds.length > 0
        ? team1.playerIds
        : [team1.id];
    const t2Players =
      Array.isArray(team2.playerIds) && team2.playerIds.length > 0
        ? team2.playerIds
        : [team2.id];

    if (!t1Players.length || !t2Players.length) {
      return;
    }

    const matchPromises = [];
    t1Players.forEach((p1Id) => {
      t2Players.forEach((p2Id) => {
        matchPromises.push(
          updateOneVsOneElo(p1Id, p2Id, team1.total, team2.total, pennantName, K_FACTOR)
        );
      });
    });
    await Promise.all(matchPromises);
  });

  await Promise.all(rankingUpdates);
}

/**
 * Updates the Elo ranking for a one-versus-one matchup between two players.
 *
 * @param {string} p1Id - Player 1's ID.
 * @param {string} p2Id - Player 2's ID.
 * @param {number} p1Score - Total score for player 1.
 * @param {number} p2Score - Total score for player 2.
 * @param {string} pennantName - The pennant name used as key in rankings.
 * @param {number} K - The Elo K-factor.
 */
async function updateOneVsOneElo(p1Id, p2Id, p1Score, p2Score, pennantName, K) {
  const p1Ref = doc(db, "members", p1Id);
  const p2Ref = doc(db, "members", p2Id);

  const [p1Snap, p2Snap] = await Promise.all([getDoc(p1Ref), getDoc(p2Ref)]);
  if (!p1Snap.exists() || !p2Snap.exists()) {
    console.warn(
      `Member doc missing - p1 exists: ${p1Snap.exists()}, p2 exists: ${p2Snap.exists()}`
    );
    return;
  }

  const p1Data = p1Snap.data();
  const p2Data = p2Snap.data();

  const p1Rank = Number(p1Data.rankings?.[pennantName]) || 100;
  const p2Rank = Number(p2Data.rankings?.[pennantName]) || 100;

  const expectedP1 = 1 / (1 + 10 ** ((p2Rank - p1Rank) / 400));
  let actualP1 = 0;
  if (p1Score > p2Score) actualP1 = 1;
  else if (p1Score === p2Score) actualP1 = 0.5;

  const newRankP1 = Math.round(p1Rank + K * (actualP1 - expectedP1));

  const expectedP2 = 1 / (1 + 10 ** ((p1Rank - p2Rank) / 400));
  let actualP2 = 0;
  if (p2Score > p1Score) actualP2 = 1;
  else if (p2Score === p1Score) actualP2 = 0.5;

  const newRankP2 = Math.round(p2Rank + K * (actualP2 - expectedP2));

  await Promise.all([
    updateDoc(p1Ref, { [`rankings.${pennantName}`]: newRankP1 }),
    updateDoc(p2Ref, { [`rankings.${pennantName}`]: newRankP2 }),
  ]);
}
